// src/features/settings/pages/BranchesPage.tsx
// PREMIUM REDESIGN: EXECUTIVE DASHBOARD, CLICKABLE CARDS, STRICT SINGLE/MULTI BUSINESS ENFORCEMENT

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { SettingsModal } from '@/features/settings/components/SettingsModal';
import * as api from '@/features/settings/services/settingsApi';
import * as businessApi from '@/features/business/services/businessApi';
import type { Business } from '@/features/business/services/businessApi';
import {
    Building2, Plus, Save, MapPin, Phone, Mail, Star, Pencil,
    ArrowLeft, Users, X, Store, Briefcase, Search,
    LayoutGrid, List, ChevronLeft, ChevronRight, Lock, Copy, Check,
    Layers, ArrowUpRight, ShieldCheck, CheckCircle2, XCircle, CreditCard
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import React from 'react';

interface Branch {
    id: string;
    tenantId?: string;
    businessId?: string;
    name: string;
    code: string;
    address: string | null;
    phone: string | null;
    email: string | null;
    isMainBranch: boolean;
    isDefault: boolean;
    isActive: boolean;
    createdAt: string;
    businessName?: string;
}

interface StaffMember {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    isActive?: boolean;
    roles?: { id?: string; name: string }[];
}

export function BranchesPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const preselectedBusinessId = searchParams.get('businessId') || '';

    const user = useAuthStore((s) => s.user);
    const tenant = useAuthStore((s) => s.tenant);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    // Filters & pagination
    const [viewMode, setViewMode] = useState<'card' | 'table'>('card');
    const [search, setSearch] = useState('');
    const [filterBusinessId, setFilterBusinessId] = useState(preselectedBusinessId);
    const [page, setPage] = useState(1);
    const limit = 12;

    // Form
    const [showForm, setShowForm] = useState(false);
    const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
    const [form, setForm] = useState({
        name: '', code: '', businessId: preselectedBusinessId, address: '', phone: '', email: '', isMainBranch: false,
    });
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    // Staff modal
    const [staffModal, setStaffModal] = useState<{ branchId: string; branchName: string } | null>(null);
    const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>([]);
    const [staffModalSearch, setStaffModalSearch] = useState('');
    const [copiedBranchId, setCopiedBranchId] = useState<string | null>(null);

    // ==================== QUERIES ====================

    const { data: businesses } = useQuery<Business[]>({
        queryKey: ['businesses-active', user?.tenantId],
        queryFn: businessApi.getBusinessesWithStats,
        enabled: !!user?.tenantId,
    });

    const { data: branchesData, isLoading, isError, refetch } = useQuery({
        queryKey: ['branches', user?.tenantId],
        queryFn: () => api.getBranches(user!.tenantId),
        enabled: !!user?.tenantId,
    });

    const { data: staffList, isLoading: isLoadingAllStaff } = useQuery<StaffMember[]>({
        queryKey: ['staff', user?.tenantId],
        queryFn: async () => {
            const result = await api.getStaff(user!.tenantId);
            if (Array.isArray(result)) return result;
            if (result?.data && Array.isArray(result.data)) return result.data;
            if (result?.users && Array.isArray(result.users)) return result.users;
            return [];
        },
        enabled: !!user?.tenantId,
    });

    const { data: assignedStaff } = useQuery<StaffMember[]>({
        queryKey: ['branch-staff', staffModal?.branchId],
        queryFn: async () => {
            const result = await api.getBranchStaff(user!.tenantId, staffModal!.branchId);
            if (Array.isArray(result)) return result;
            if (result?.data && Array.isArray(result.data)) return result.data;
            return [];
        },
        enabled: !!staffModal?.branchId,
    });

    // ==================== DERIVED DATA ====================

    const allBranches: Branch[] = (() => {
        const raw = branchesData;
        if (Array.isArray(raw)) return raw;
        if (raw?.data && Array.isArray(raw.data)) return raw.data;
        return [];
    })();

    // Filter by business and search
    const filteredBranches = allBranches.filter(b => {
        if (filterBusinessId && b.businessId !== filterBusinessId) return false;
        if (search) {
            const q = search.toLowerCase();
            const matchesName = b.name.toLowerCase().includes(q);
            const matchesCode = b.code.toLowerCase().includes(q);
            const matchesAddress = b.address?.toLowerCase().includes(q);
            if (!matchesName && !matchesCode && !matchesAddress) return false;
        }
        return true;
    });

    const totalCount = filteredBranches.length;
    const totalPages = Math.ceil(totalCount / limit);
    const paginatedBranches = filteredBranches.slice((page - 1) * limit, page * limit);

    const activeBranches = allBranches.filter(b => b.isActive).length;
    const mainBranchesCount = allBranches.filter(b => b.isMainBranch).length;
    const selectedBusiness = businesses?.find(b => b.id === filterBusinessId);

    // Business model check
    const isSingleModel = tenant?.businessModel === 'single';
    const canCreateGlobalBranch = !isSingleModel || allBranches.length === 0;

    // ==================== MUTATIONS ====================

    const saveMutation = useMutation({
        mutationFn: async () => {
            const payload: any = {
                name: form.name.trim(),
                code: form.code.trim().toUpperCase(),
                businessId: form.businessId,
                address: form.address.trim() || undefined,
                phone: form.phone.trim() || undefined,
                email: form.email.trim() || undefined,
                isMainBranch: form.isMainBranch,
            };
            if (editingBranch) return api.updateBranch(user!.tenantId, editingBranch.id, payload);
            return api.createBranch(user!.tenantId, payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['branches'] });
            queryClient.invalidateQueries({ queryKey: ['businesses'] });
            queryClient.invalidateQueries({ queryKey: ['businesses-stats'] });
            queryClient.invalidateQueries({ queryKey: ['business'] });

            addToast({
                type: 'success',
                title: editingBranch ? 'Branch Updated ✨' : 'Branch Created 🏪',
                message: `${form.name} has been ${editingBranch ? 'updated' : 'created'}.`
            });
            handleCloseForm();
        },
        onError: (err: any) => {
            const errorDetails = err?.response?.data?.error;
            if (errorDetails?.details && Array.isArray(errorDetails.details)) {
                const fieldErrors: Record<string, string> = {};
                errorDetails.details.forEach((d: any) => {
                    if (d.field) fieldErrors[d.field] = d.message;
                });
                setFormErrors(fieldErrors);
            } else {
                const msg = (errorDetails?.message || err?.message || '').toLowerCase();
                if (msg.includes('code')) setFormErrors({ code: errorDetails?.message || 'Branch code already in use' });
                else if (msg.includes('name')) setFormErrors({ name: errorDetails?.message || 'Branch name error' });
                else if (msg.includes('business')) setFormErrors({ businessId: errorDetails?.message || 'Invalid business' });
                else if (msg.includes('single')) setFormErrors({ businessId: errorDetails?.message || 'Single businesses cannot have multiple branches.' });
            }
            const msg = errorDetails?.message || err?.message || 'Failed to save branch';
            addToast({ type: 'error', title: 'Save Failed', message: msg });
        },
    });

    const assignStaffMutation = useMutation({
        mutationFn: () => api.assignStaffToBranch(user!.tenantId, staffModal!.branchId, selectedStaffIds),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['branch-staff', staffModal?.branchId] });
            queryClient.invalidateQueries({ queryKey: ['branches'] });
            queryClient.invalidateQueries({ queryKey: ['businesses'] });
            queryClient.invalidateQueries({ queryKey: ['businesses-stats'] });
            queryClient.invalidateQueries({ queryKey: ['business'] });

            addToast({
                type: 'success',
                title: 'Branch Staff Assigned',
                message: `${selectedStaffIds.length} staff member(s) assigned to ${staffModal?.branchName}.`
            });
            closeStaffModal();
        },
        onError: (err: any) => addToast({
            type: 'error',
            title: 'Assignment Failed',
            message: err?.response?.data?.error?.message || 'An error occurred during staff assignment'
        }),
    });

    // ==================== HANDLERS ====================

    useEffect(() => {
        if (assignedStaff && Array.isArray(assignedStaff)) {
            setSelectedStaffIds(assignedStaff.map((s: StaffMember) => s.id));
        }
    }, [assignedStaff]);

    const handleCopyCode = (branch: Branch, e: React.MouseEvent) => {
        e.stopPropagation();
        navigator.clipboard.writeText(branch.code);
        setCopiedBranchId(branch.id);
        setTimeout(() => setCopiedBranchId(null), 2000);
        addToast({ type: 'info', title: 'Code Copied', message: `${branch.code} copied to clipboard.` });
    };

    const handleEdit = (branch: Branch, e: React.MouseEvent) => {
        e.stopPropagation();
        setEditingBranch(branch);
        setForm({
            name: branch.name,
            code: branch.code,
            businessId: branch.businessId || '',
            address: branch.address || '',
            phone: branch.phone || '',
            email: branch.email || '',
            isMainBranch: branch.isMainBranch,
        });
        setFormErrors({});
        setShowForm(true);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setEditingBranch(null);
        setForm({
            name: '', code: '', businessId: filterBusinessId, address: '', phone: '', email: '', isMainBranch: false
        });
        setFormErrors({});
    };

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};
        if (!form.name.trim()) errors.name = 'Branch name is required';
        if (!form.code.trim()) errors.code = 'Branch code is required';
        if (!form.businessId) errors.businessId = 'Please select a business';

        // Check if selected business is single-type and already has branches
        if (form.businessId && !editingBranch) {
            const targetBiz = businesses?.find(b => b.id === form.businessId);
            if (targetBiz?.type?.toLowerCase() === 'single') {
                const existingInBiz = allBranches.filter(b => b.businessId === form.businessId);
                if (existingInBiz.length >= 1) {
                    errors.businessId = 'This business is marked as Single-type and already has a primary branch.';
                }
            }
        }

        setFormErrors(errors);
        if (Object.keys(errors).length > 0) {
            addToast({ type: 'warning', title: 'Form Incomplete', message: 'Please correct the issues before saving.' });
            return false;
        }
        return true;
    };

    const openStaffModal = async (branch: Branch, e: React.MouseEvent) => {
        e.stopPropagation();
        setStaffModal({ branchId: branch.id, branchName: branch.name });
        setStaffModalSearch('');
        try {
            const res = await api.getBranchStaff(user!.tenantId, branch.id);
            const list = Array.isArray(res) ? res : (res as any)?.data || [];
            setSelectedStaffIds(list.map((s: any) => s.id));
        } catch {
            setSelectedStaffIds([]);
        }
    };

    const closeStaffModal = () => {
        setStaffModal(null);
        setSelectedStaffIds([]);
        setStaffModalSearch('');
    };

    const toggleStaffSelection = (staffId: string) => {
        setSelectedStaffIds(prev => prev.includes(staffId) ? prev.filter(id => id !== staffId) : [...prev, staffId]);
    };

    // ==================== LOADING / ERROR ====================

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Spinner size="xl" variant="emerald" />
            </div>
        );
    }

    if (isError) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6">
                <ErrorState title="Failed to load branches" onRetry={() => refetch()} />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/20">
            <div className="max-w-[1600px] mx-auto p-4 lg:p-6 space-y-6">

                {/* Breadcrumb navigation */}
                <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
                    <button
                        onClick={() => navigate('/businesses')}
                        className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-emerald-700 group transition-colors">
                        <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
                        Back to Businesses
                    </button>
                </motion.div>

                {/* ========== EXECUTIVE HERO BANNER ========== */}
                <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="relative overflow-hidden rounded-3xl shadow-2xl bg-gradient-to-br from-teal-700 via-emerald-600 to-teal-800 text-white">

                    {/* Ambient Glows */}
                    <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.2),transparent_50%)]" />
                        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
                        <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-teal-400/20 rounded-full blur-3xl" />
                    </div>

                    <div className="relative px-6 lg:px-10 py-8 lg:py-10">
                        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">

                            {/* Left Info */}
                            <div className="flex items-center gap-4">
                                <motion.div
                                    whileHover={{ scale: 1.05, rotate: -2 }}
                                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-3xl flex items-center justify-center bg-white/15 backdrop-blur-md ring-1 ring-white/30 shadow-2xl flex-shrink-0">
                                    <Store size={36} className="text-white" />
                                </motion.div>
                                <div>
                                    <div className="flex items-center gap-2.5 flex-wrap">
                                        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight">
                                            Branches & Locations
                                        </h1>
                                        {isSingleModel ? (
                                            <Badge className="bg-amber-400/20 text-amber-200 border-amber-300/30 font-medium">
                                                <Lock size={10} className="mr-1 inline" /> Single-Business Account
                                            </Badge>
                                        ) : (
                                            <Badge className="bg-white/15 text-white border-white/20 font-medium">
                                                <Layers size={10} className="mr-1 inline" /> Multi-Location Enterprise
                                            </Badge>
                                        )}
                                    </div>
                                    <p className="text-teal-100/90 text-sm mt-1">
                                        {allBranches.length} registered location{allBranches.length !== 1 ? 's' : ''} across {businesses?.length || 0} business entities
                                        {selectedBusiness && <> · Filtering for <strong className="text-white">{selectedBusiness.name}</strong></>}
                                    </p>
                                </div>
                            </div>

                            {/* Right Action */}
                            <div className="flex items-center gap-3">
                                {!canCreateGlobalBranch ? (
                                    <div className="px-4 py-2.5 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs font-semibold flex items-center gap-2 shadow-sm">
                                        <Lock size={14} className="text-amber-300" />
                                        <span>Single Business Limit (1/1 Branch)</span>
                                    </div>
                                ) : (
                                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                                        <Button
                                            size="md"
                                            leftIcon={<Plus size={16} />}
                                            onClick={() => { handleCloseForm(); setShowForm(true); }}
                                            className="!bg-white !text-teal-800 hover:!bg-teal-50 !font-black !px-6 !py-3 !rounded-2xl !shadow-xl shadow-black/20">
                                            Add Branch
                                        </Button>
                                    </motion.div>
                                )}
                            </div>
                        </div>

                        {/* Quick Stats Ribbon */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8 pt-6 border-t border-white/15">
                            {[
                                { label: 'Total Branches', value: allBranches.length, icon: Building2 },
                                { label: 'Active Roster', value: activeBranches, icon: CheckCircle2 },
                                { label: 'Inactive', value: allBranches.length - activeBranches, icon: XCircle },
                                { label: 'Main Headquarters', value: mainBranchesCount, icon: Star },
                            ].map(stat => (
                                <div key={stat.label} className="bg-white/10 rounded-2xl p-3 backdrop-blur-sm border border-white/10">
                                    <div className="flex items-center gap-1.5 text-teal-200 text-[10px] uppercase font-bold tracking-wider mb-1">
                                        <stat.icon size={12} />
                                        <span>{stat.label}</span>
                                    </div>
                                    <p className="text-lg font-black text-white">{stat.value}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </motion.div>

                {/* ========== TOOLBAR & FILTERS ========== */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-white rounded-2xl border border-slate-200 shadow-sm p-3">
                    <div className="flex items-center gap-2.5 flex-wrap w-full sm:w-auto">
                        <div className="relative flex-1 sm:flex-initial">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search by name, code, address..."
                                value={search}
                                onChange={e => { setSearch(e.target.value); setPage(1); }}
                                className="pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs w-full sm:w-60 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                            />
                            {search && (
                                <button
                                    onClick={() => setSearch('')}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                    <X size={12} />
                                </button>
                            )}
                        </div>

                        <select
                            value={filterBusinessId}
                            onChange={e => { setFilterBusinessId(e.target.value); setPage(1); }}
                            className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:ring-2 focus:ring-teal-500/20">
                            <option value="">All Businesses</option>
                            {(businesses || []).map(b => (
                                <option key={b.id} value={b.id}>
                                    {b.name} ({b.type === 'single' ? 'Single' : 'Multi'})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center gap-2 self-end sm:self-auto">
                        <span className="text-xs text-slate-400 font-medium">
                            {filteredBranches.length} location{filteredBranches.length !== 1 ? 's' : ''}
                        </span>
                        <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
                            <button
                                onClick={() => setViewMode('card')}
                                className={clsx(
                                    'p-1.5 rounded-lg transition-all',
                                    viewMode === 'card'
                                        ? 'bg-white shadow-xs text-teal-700 font-bold'
                                        : 'text-slate-400 hover:text-slate-600'
                                )}>
                                <LayoutGrid size={15} />
                            </button>
                            <button
                                onClick={() => setViewMode('table')}
                                className={clsx(
                                    'p-1.5 rounded-lg transition-all',
                                    viewMode === 'table'
                                        ? 'bg-white shadow-xs text-teal-700 font-bold'
                                        : 'text-slate-400 hover:text-slate-600'
                                )}>
                                <List size={15} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* ========== BRANCH LIST / CARDS ========== */}
                {paginatedBranches.length === 0 ? (
                    <EmptyState
                        icon={<Store size={44} className="text-teal-600" />}
                        title={search || filterBusinessId ? 'No matching branches' : 'No branches yet'}
                        description={search || filterBusinessId ? 'Try adjusting your search query or business filter.' : 'Create your first branch location to begin operations.'}
                        action={!search && !filterBusinessId && canCreateGlobalBranch ? {
                            label: 'Add Branch',
                            onClick: () => setShowForm(true)
                        } : undefined}
                    />
                ) : viewMode === 'table' ? (
                    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100">
                                <thead>
                                    <tr className="bg-slate-50/70">
                                        <th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Branch Details</th>
                                        <th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Business Entity</th>
                                        <th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Code</th>
                                        <th className="px-6 py-4 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-4 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {paginatedBranches.map(branch => (
                                        <tr
                                            key={branch.id}
                                            onClick={() => navigate(`/branches/${branch.id}`)}
                                            className="hover:bg-teal-50/40 cursor-pointer transition-colors group">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-xl bg-teal-100 text-teal-800 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                                                        <Store size={18} />
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <p className="text-sm font-bold text-slate-900 group-hover:text-teal-700 transition-colors">
                                                                {branch.name}
                                                            </p>
                                                            {branch.isMainBranch && (
                                                                <Badge variant="warning" size="sm">Main</Badge>
                                                            )}
                                                        </div>
                                                        {branch.address && (
                                                            <p className="text-xs text-slate-400 mt-0.5 truncate max-w-xs">{branch.address}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <Badge variant="neutral" size="sm">
                                                    {branch.businessName || 'Business'}
                                                </Badge>
                                            </td>
                                            <td className="px-6 py-4">
                                                <code className="text-xs font-mono font-bold bg-slate-100 text-slate-700 px-2 py-0.5 rounded border border-slate-200">
                                                    {branch.code}
                                                </code>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <Badge variant={branch.isActive ? 'success' : 'danger'} size="sm">
                                                    {branch.isActive ? 'Active' : 'Inactive'}
                                                </Badge>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end gap-1.5" onClick={e => e.stopPropagation()}>
                                                    <button
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            navigate(`/branches/${branch.id}?tab=accounts`);
                                                        }}
                                                        className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-700 hover:bg-emerald-50 transition-colors"
                                                        title="Branch Bank Accounts">
                                                        <CreditCard size={15} />
                                                    </button>
                                                    <button
                                                        onClick={e => handleEdit(branch, e)}
                                                        className="p-1.5 rounded-lg text-slate-400 hover:text-teal-700 hover:bg-teal-50 transition-colors"
                                                        title="Edit Branch">
                                                        <Pencil size={15} />
                                                    </button>
                                                    <button
                                                        onClick={e => openStaffModal(branch, e)}
                                                        className="p-1.5 rounded-lg text-slate-400 hover:text-purple-700 hover:bg-purple-50 transition-colors"
                                                        title="Manage Staff">
                                                        <Users size={15} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {paginatedBranches.map((branch, i) => (
                            <motion.div
                                key={branch.id}
                                initial={{ opacity: 0, y: 15 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.03 }}
                                whileHover={{ y: -4 }}
                                onClick={() => navigate(`/branches/${branch.id}`)}
                                className="bg-white rounded-3xl border-2 border-slate-200 hover:border-teal-400 shadow-sm hover:shadow-xl transition-all cursor-pointer group flex flex-col justify-between overflow-hidden">
                                <div>
                                    {/* Top colored strip for main branch */}
                                    {branch.isMainBranch && (
                                        <div className="h-1.5 bg-gradient-to-r from-amber-400 via-orange-500 to-amber-500" />
                                    )}

                                    <div className="p-6">
                                        <div className="flex items-start justify-between gap-3 mb-4">
                                            <div className="flex items-center gap-3">
                                                <div className={clsx(
                                                    'w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105 shadow-xs',
                                                    branch.isMainBranch ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white' : 'bg-teal-100 text-teal-800'
                                                )}>
                                                    <Store size={22} />
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        <h3 className="font-extrabold text-slate-900 text-base group-hover:text-teal-700 transition-colors">
                                                            {branch.name}
                                                        </h3>
                                                    </div>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <button
                                                            onClick={e => handleCopyCode(branch, e)}
                                                            className="text-xs font-mono font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 px-2 py-0.5 rounded border border-slate-200 inline-flex items-center gap-1 transition-colors">
                                                            <span>{branch.code}</span>
                                                            {copiedBranchId === branch.id ? <Check size={10} className="text-emerald-600" /> : <Copy size={10} className="text-slate-400" />}
                                                        </button>
                                                        {branch.businessName && (
                                                            <span className="text-xs text-slate-400 font-medium truncate max-w-[120px]">
                                                                {branch.businessName}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex flex-col items-end gap-1">
                                                {branch.isMainBranch && (
                                                    <Badge variant="warning" size="sm">Main</Badge>
                                                )}
                                                <Badge variant={branch.isActive ? 'success' : 'danger'} size="sm">
                                                    {branch.isActive ? 'Active' : 'Inactive'}
                                                </Badge>
                                            </div>
                                        </div>

                                        <div className="space-y-1.5 text-xs text-slate-500 pt-1">
                                            {branch.address && (
                                                <p className="flex items-center gap-1.5 truncate">
                                                    <MapPin size={12} className="text-teal-600 flex-shrink-0" />
                                                    <span className="truncate">{branch.address}</span>
                                                </p>
                                            )}
                                            {branch.phone && (
                                                <p className="flex items-center gap-1.5">
                                                    <Phone size={12} className="text-teal-600 flex-shrink-0" />
                                                    <span>{branch.phone}</span>
                                                </p>
                                            )}
                                            {branch.email && (
                                                <p className="flex items-center gap-1.5 truncate">
                                                    <Mail size={12} className="text-teal-600 flex-shrink-0" />
                                                    <span className="truncate">{branch.email}</span>
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Bottom action bar */}
                                <div className="px-6 py-3.5 bg-slate-50/70 border-t border-slate-100 flex items-center justify-between">
                                    <span className="text-xs font-semibold text-teal-700 flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
                                        View Dashboard <ArrowUpRight size={13} />
                                    </span>

                                    <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                                        <button
                                            onClick={e => {
                                                e.stopPropagation();
                                                navigate(`/branches/${branch.id}?tab=accounts`);
                                            }}
                                            className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 hover:bg-emerald-100/70 px-2.5 py-1 rounded-lg transition-colors flex items-center gap-1"
                                            title="View and assign bank accounts">
                                            <CreditCard size={11} /> Banks
                                        </button>
                                        <button
                                            onClick={e => handleEdit(branch, e)}
                                            className="text-xs font-semibold text-slate-600 hover:text-teal-700 hover:bg-slate-200/70 px-2.5 py-1 rounded-lg transition-colors">
                                            Edit
                                        </button>
                                        <button
                                            onClick={e => openStaffModal(branch, e)}
                                            className="text-xs font-semibold text-purple-600 hover:text-purple-800 hover:bg-purple-100/70 px-2.5 py-1 rounded-lg transition-colors">
                                            Staff
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}

                {/* ========== PAGINATION ========== */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between bg-white rounded-2xl border border-slate-200 shadow-sm px-6 py-4">
                        <p className="text-xs font-semibold text-slate-500">
                            Showing {(page - 1) * limit + 1}-{Math.min(page * limit, totalCount)} of {totalCount} locations
                        </p>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="p-2 rounded-lg hover:bg-slate-50 disabled:opacity-30">
                                <ChevronLeft size={16} />
                            </button>
                            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => (
                                <button
                                    key={i + 1}
                                    onClick={() => setPage(i + 1)}
                                    className={clsx(
                                        'w-8 h-8 rounded-lg text-xs font-bold transition-all',
                                        page === i + 1 ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                                    )}>
                                    {i + 1}
                                </button>
                            ))}
                            <button
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                                className="p-2 rounded-lg hover:bg-slate-50 disabled:opacity-30">
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )}

                {/* ========== CREATE/EDIT BRANCH MODAL ========== */}
                <SettingsModal
                    open={showForm}
                    onClose={handleCloseForm}
                    title={editingBranch ? 'Edit Branch' : 'Register New Branch'}
                    subtitle={editingBranch ? `Modifying location ${editingBranch.name}` : 'Create an operating location for your business'}
                    icon={Store}
                    footer={
                        <>
                            <Button variant="secondary" onClick={handleCloseForm}>Cancel</Button>
                            <Button
                                leftIcon={<Save size={15} />}
                                onClick={() => { if (validateForm()) saveMutation.mutate(); }}
                                isLoading={saveMutation.isPending}
                                disabled={!form.name || !form.code || !form.businessId}
                                className="bg-teal-600 hover:bg-teal-700 text-white font-semibold">
                                {editingBranch ? 'Save Changes' : 'Create Branch'}
                            </Button>
                        </>
                    }>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wider">
                                Assigned Business Entity *
                            </label>
                            <select
                                value={form.businessId}
                                onChange={e => setForm(prev => ({ ...prev, businessId: e.target.value }))}
                                disabled={!!editingBranch}
                                className={clsx(
                                    'w-full px-4 py-2.5 border-2 rounded-xl text-sm bg-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500',
                                    formErrors.businessId ? 'border-rose-300' : 'border-slate-200'
                                )}>
                                <option value="">Select a business</option>
                                {(businesses || []).map(b => {
                                    const isSingle = b.type?.toLowerCase() === 'single';
                                    const branchesCount = allBranches.filter(br => br.businessId === b.id).length;
                                    const isLimitReached = isSingle && branchesCount >= 1 && (!editingBranch || editingBranch.businessId !== b.id);
                                    return (
                                        <option key={b.id} value={b.id} disabled={isLimitReached}>
                                            {b.name} ({b.shortCode}) {isSingle ? '— Single-type (1 max)' : '— Multi'} {isLimitReached ? '[LIMIT REACHED]' : ''}
                                        </option>
                                    );
                                })}
                            </select>
                            {formErrors.businessId && (
                                <p className="text-xs text-rose-500 mt-1 font-medium">{formErrors.businessId}</p>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <Input
                                label="Branch Name *"
                                value={form.name}
                                error={formErrors.name}
                                onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Victoria Island Branch"
                                required
                            />
                            <Input
                                label="Branch Code *"
                                value={form.code}
                                error={formErrors.code}
                                onChange={e => setForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                                placeholder="VI-01"
                                required
                            />
                        </div>

                        <Input
                            label="Physical Address"
                            value={form.address}
                            onChange={e => setForm(prev => ({ ...prev, address: e.target.value }))}
                            placeholder="Plot 4, Adeola Odeku St"
                            leftIcon={<MapPin size={14} />}
                        />

                        <div className="grid grid-cols-2 gap-3">
                            <Input
                                label="Phone Number"
                                value={form.phone}
                                onChange={e => setForm(prev => ({ ...prev, phone: e.target.value }))}
                                placeholder="+234..."
                            />
                            <Input
                                label="Email Address"
                                value={form.email}
                                onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
                                placeholder="branch@domain.com"
                            />
                        </div>

                        <label className="flex items-center gap-3 cursor-pointer p-3 bg-slate-50 rounded-2xl border border-slate-200">
                            <input
                                type="checkbox"
                                checked={form.isMainBranch}
                                onChange={e => setForm(prev => ({ ...prev, isMainBranch: e.target.checked }))}
                                className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                            />
                            <div>
                                <span className="text-sm font-bold text-slate-800">Designate as Main Branch</span>
                                <p className="text-xs text-slate-500">Primary operating location for this business.</p>
                            </div>
                        </label>
                    </div>
                </SettingsModal>

                {/* ========== STAFF ASSIGNMENT MODAL ========== */}
                <AnimatePresence>
                    {staffModal && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                            onClick={closeStaffModal}>
                            <motion.div
                                initial={{ scale: 0.95, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.95, opacity: 0 }}
                                onClick={e => e.stopPropagation()}
                                className="bg-white rounded-3xl shadow-2xl w-full max-w-xl max-h-[85vh] flex flex-col overflow-hidden border border-slate-100">
                                <div className="px-7 py-5 bg-gradient-to-r from-teal-700 via-emerald-600 to-teal-800 text-white flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-xl bg-white/20">
                                            <Users size={20} className="text-white" />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-bold text-white">Assign Staff to Branch</h2>
                                            <p className="text-teal-100 text-xs">{staffModal.branchName}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={closeStaffModal}
                                        className="p-2 rounded-xl bg-white/15 text-white hover:bg-white/30 transition-colors">
                                        <X size={18} />
                                    </button>
                                </div>

                                <div className="p-6 space-y-4 overflow-y-auto flex-1">
                                    <div className="flex items-center justify-between gap-2">
                                        <p className="text-xs text-slate-500 font-medium">
                                            Select staff members authorized to operate at this location ({selectedStaffIds.length} assigned)
                                        </p>
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setSelectedStaffIds((staffList || []).map(s => s.id))}
                                                className="text-[11px] font-semibold text-teal-700 hover:underline">
                                                Select All
                                            </button>
                                            <span className="text-slate-300">•</span>
                                            <button
                                                type="button"
                                                onClick={() => setSelectedStaffIds([])}
                                                className="text-[11px] font-semibold text-slate-500 hover:underline">
                                                Clear
                                            </button>
                                        </div>
                                    </div>

                                    {/* Search input */}
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
                                        <input
                                            type="text"
                                            placeholder="Search staff by name, email, or role..."
                                            value={staffModalSearch}
                                            onChange={e => setStaffModalSearch(e.target.value)}
                                            className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                                        />
                                    </div>

                                    {/* Staff list */}
                                    <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                                        {isLoadingAllStaff ? (
                                            <div className="py-8 text-center"><Spinner size="md" /></div>
                                        ) : (staffList || []).filter(s => {
                                            if (!staffModalSearch) return true;
                                            const term = staffModalSearch.toLowerCase();
                                            return s.firstName?.toLowerCase().includes(term) ||
                                                s.lastName?.toLowerCase().includes(term) ||
                                                s.email?.toLowerCase().includes(term) ||
                                                s.roles?.some(r => r.name.toLowerCase().includes(term));
                                        }).length === 0 ? (
                                            <p className="text-center py-6 text-xs text-slate-400">No staff found matching search.</p>
                                        ) : (
                                            (staffList || [])
                                                .filter(s => {
                                                    if (!staffModalSearch) return true;
                                                    const term = staffModalSearch.toLowerCase();
                                                    return s.firstName?.toLowerCase().includes(term) ||
                                                        s.lastName?.toLowerCase().includes(term) ||
                                                        s.email?.toLowerCase().includes(term) ||
                                                        s.roles?.some(r => r.name.toLowerCase().includes(term));
                                                })
                                                .map(staff => {
                                                    const isSelected = selectedStaffIds.includes(staff.id);
                                                    return (
                                                        <label
                                                            key={staff.id}
                                                            className={clsx(
                                                                'flex items-center gap-3 p-3 rounded-2xl border-2 cursor-pointer transition-all',
                                                                isSelected
                                                                    ? 'border-teal-500 bg-teal-50/70 shadow-xs'
                                                                    : 'border-slate-100 hover:border-slate-200 hover:bg-slate-50/60'
                                                            )}>
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => toggleStaffSelection(staff.id)}
                                                                className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                                                            />
                                                            <div className="w-8 h-8 rounded-xl bg-teal-100 text-teal-800 font-bold flex items-center justify-center text-xs flex-shrink-0">
                                                                {staff.firstName?.[0]}{staff.lastName?.[0]}
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                <div className="flex items-center gap-2">
                                                                    <p className="text-xs font-bold text-slate-800 truncate">
                                                                        {staff.firstName} {staff.lastName}
                                                                    </p>
                                                                    {staff.roles?.map(r => (
                                                                        <span key={r.id || r.name} className="px-1.5 py-0.2 rounded text-[9px] font-medium bg-slate-200 text-slate-700">
                                                                            {r.name}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                                <p className="text-[11px] text-slate-400 truncate">{staff.email}</p>
                                                            </div>
                                                            <Badge variant={staff.isActive ? 'success' : 'danger'} size="sm">
                                                                {staff.isActive ? 'Active' : 'Inactive'}
                                                            </Badge>
                                                        </label>
                                                    );
                                                })
                                        )}
                                    </div>
                                </div>

                                <div className="px-7 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                                    <span className="text-xs text-slate-500 font-medium">{selectedStaffIds.length} staff selected</span>
                                    <div className="flex gap-2">
                                        <Button variant="secondary" size="sm" onClick={closeStaffModal}>
                                            Cancel
                                        </Button>
                                        <Button
                                            size="sm"
                                            onClick={() => assignStaffMutation.mutate()}
                                            isLoading={assignStaffMutation.isPending}
                                            leftIcon={<Save size={14} />}
                                            className="bg-teal-600 hover:bg-teal-700 text-white font-semibold">
                                            Save Assignments
                                        </Button>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}